import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, FormControl, InputLabel, Select, MenuItem,
  Checkbox, Tabs, Tab
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../../Sidebar/DashboardLayout';
import axios from 'axios';
import { menuByRole } from '../../../commun/sharedMenuConfig';
import './RolesPage.css';
import Chatbox from '../../../commun/ChatBox';

const permissionOptions = ['-', 'Oui', 'Non'];
const viewEditDeleteOptions = ['-', 'Tous', 'Soi'];

const RolesPage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState({});
  const [message, setMessage] = useState('');
  const userRole = localStorage.getItem('userType');

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/roles`);
      setRoles(res.data);
      const defaultRole = res.data.find(r => r.name === 'admin') || res.data[0];
      if (defaultRole) setSelectedRole(defaultRole.name);
    } catch {}
  };

  const fetchPermissions = async (roleName) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/roles/${roleName}/permissions`);
      setPermissions(res.data || {});
    } catch {
      const defaultPerms = {};
      const roleMenus = menuByRole[roleName.toLowerCase()] || [];
      roleMenus.forEach(menu => {
        defaultPerms[menu.name] = { Accès: false, Créer: '-', Voir: '-', Modifier: '-', Supprimer: '-' };
      });
      setPermissions(defaultPerms);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) fetchPermissions(selectedRole);
  }, [selectedRole]);

  const handleAddRole = async () => {
    if (!newRole.trim()) {
      setError("Le nom du rôle est requis.");
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/roles`, { name: newRole.trim() });
      setNewRole('');
      setError('');
      fetchRoles();
      setMessage("Rôle ajouté avec succès");
    } catch (err) {
      if (err.response?.status === 409) {
        setError("Ce rôle existe déjà.");
      } else {
        setError("Erreur lors de l'ajout.");
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/roles/${id}`);
      fetchRoles();
      setMessage("Rôle supprimé");
    } catch {}
  };

  const handleChangePermission = (menu, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [menu]: {
        ...prev[menu],
        [action]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/roles/${selectedRole}/permissions`, permissions);
      setMessage("Permissions enregistrées");
    } catch {
      setMessage("Erreur lors de l'enregistrement");
    }
  };

  const currentMenus = selectedRole ? (menuByRole[selectedRole.toLowerCase()] || []) : [];
  const isAdmin = selectedRole === 'admin';

  return (
    <DashboardLayout>
      <Box className="roles-page">
        <Tabs value={tabIndex} onChange={(e, newIndex) => setTabIndex(newIndex)}>
          <Tab label="Gestion des rôles" />
          <Tab label="Permissions par rôle" />
        </Tabs>

        {tabIndex === 0 && (
          <>
            <Typography variant="h5" className="section-title">Ajouter / Supprimer des rôles</Typography>
            <Box className="add-role-form">
              <TextField
                label="Nouveau rôle"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                error={!!error}
                helperText={error}
              />
              <Button variant="contained" onClick={handleAddRole}>Ajouter</Button>
            </Box>
            <Paper className="roles-table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom du rôle</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map(role => (
                    <TableRow key={role._id}>
                      <TableCell>{role.name}</TableCell>
                      <TableCell align="right">
                        {role.name !== 'admin' && (
                          <IconButton onClick={() => handleDelete(role._id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {tabIndex === 1 && (
          <>
            <Typography variant="h5" className="section-title">Permissions par rôle</Typography>
            <FormControl className="role-select">
              <InputLabel>Rôle</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Rôle"
              >
                {roles.map(role => (
                  <MenuItem key={role._id} value={role.name}>{role.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRole && (
              <Paper className="roles-table-container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Menu</TableCell>
                      <TableCell>Accès</TableCell>
                      <TableCell>Créer</TableCell>
                      <TableCell>Voir</TableCell>
                      <TableCell>Modifier</TableCell>
                      <TableCell>Supprimer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentMenus.map(({ name, label }) => {
                      const perms = permissions[name] || {};
                      const access = isAdmin ? true : !!perms['Accès'];

                      return (
                        <TableRow key={name}>
                          <TableCell>{label}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={access}
                              disabled={isAdmin}
                              onChange={(e) => handleChangePermission(name, 'Accès', e.target.checked)}
                            />
                          </TableCell>
                          {['Créer', 'Voir', 'Modifier', 'Supprimer'].map(action => (
                            <TableCell align="center" key={action}>
                              <FormControl className="permission-select">
                                <Select
                                  value={
                                    isAdmin 
                                      ? (action === 'Créer' ? 'Oui' : 'Tous') 
                                      : perms[action] || '-'
                                  }
                                  disabled={isAdmin || !access}
                                  onChange={(e) => handleChangePermission(name, action, e.target.value)}
                                >
                                  {(action === 'Créer' ? permissionOptions : viewEditDeleteOptions).map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="save-button-wrapper">
                  <Button variant="contained" onClick={handleSavePermissions}>Enregistrer</Button>
                  {message && <span className="message-inline">{message}</span>}
                </div>
              </Paper>
            )}
          </>
        )}
      </Box>
      {userRole !== 'agent' && <Chatbox userRole={userRole} />}
    </DashboardLayout>
  );
};

export default RolesPage;
